import React from 'react';
import Head from 'next/head';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import BTE from 'lib/BTE';
import { MOBILE_ACQUISITION_BANNER } from 'lib/brandFeatures.json';
import { ArticleContext } from 'lib/ContextTypes';
import { article as ArticlePropType } from 'lib/CustomPropTypes';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import getPathName from 'lib/getPathName';
import LegacyIDService from 'lib/LegacyIDService';
import { navbar, NAVBAR_THEME } from 'lib/navbar';
import { sectionContainSlug, extractTermPaths } from 'lib/taxonomy';
import { publicoHeadlineBrands } from 'lib/vertical';

import AdsBundle from 'components/AdsBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import ArticleHero from 'components/Article/ArticleHero';
import ArticleBody from 'components/Article/ArticleBody';
import ErrorBoundary from 'components/ErrorBoundary';
import IntObserver from 'components/IntObserver';
import PopupModule from 'components/NewsletterSignup/PopupModule';
import MobileBanner from 'components/MobileBanner';
import { ArticleMetadata, GlobalMetadata } from 'components/PageMetadata';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';
import ScrollingAnalytics from 'components/ScrollingAnalytics';
import IconfontStyleTag from 'components/IconfontStyleTag';
import UniversalCheckout from 'components/UniversalCheckout';

import { loadArticle } from 'redux/modules/article';
import { bulkUpdate as navUpdateAction } from 'redux/modules/navbar';
import {
  disableLazyLoadAction,
  setChromeless,
  setPageView,
  setVertical,
} from 'redux/modules/shared';
import { globalLoadComplete } from 'redux/modules/router';

import ErrorPage from '../_error';

import 'assets/stylesheets/main.scss';
import 'assets/stylesheets/toolkit.scss';
import styles from './article.module.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';

const block = 'article';
const pageView = 'article';
const externalLink = 'externalLink';

const navbarConfig = {
  theme({ content, vertical }) {
    if (Object.keys(content).length) {
      const {
        source,
        taxonomy: { sections },
      } = content;

      const isShoppingSection = sectionContainSlug(sections, 'shopping');
      if (isShoppingSection) {
        return NAVBAR_THEME.LIGHT;
      }

      const getShoppingSourceName = get(source, 'organization.name', false);
      if (vertical === 'news' && getShoppingSourceName === 'StackCommerce') {
        return NAVBAR_THEME.VERTICAL;
      }
    }

    if (['knowyourvalue', 'today'].indexOf(vertical) >= 0) {
      return NAVBAR_THEME.LIGHT;
    }
    return NAVBAR_THEME.VERTICAL;
  },
  headline: ({ content }) => get(content, ['headline'], null),
  activeAt: 160,
  showShortcuts: ({ content }) => {
    const primaryTopic = get(content, 'taxonomy.primaryTopic');
    if (primaryTopic) {
      return !extractTermPaths(primaryTopic).includes('nbcnews/section/politics/2020-election');
    }
    return true;
  },
};

const mapStateToProps = ({
  article, shared, router, navbar: navStore,
}) => ({
  articles: article.content,
  vertical: shared.vertical,
  isChromeless: shared.isChromeless,
  path: router.path,
  active: navStore.active,
});

const mapActionsToProps = (dispatch) => ({
  navUpdate: (theme) => dispatch(navUpdateAction(theme)),
});

class ArticlePage extends React.Component {
  static async getInitialProps(ctx) {
    const {
      params: { id: rawID },
      query: {
        chromeless,
        lazyload,
      },
    } = ctx.req;
    const {
      locals: {
        domain, vertical, hostname, services,
      },
    } = ctx.res;
    const { store, asPath } = ctx;
    const id = LegacyIDService.article(rawID);
    // const {
    //   query: { getLaunchDarklyClient },
    // } = ctx;

    // // TODO: Remove this example when there is a real flag to use as a pattern

    // // Example of how to use a feature flag in a Page component:

    // inside of getInitialProps, get the launch darkly client
    // const launchDarklyClient = await getLaunchDarklyClient();

    // get the value of your flag
    // const myFlag = await launchDarklyClient.variation(
    //   'my-flag',
    //   // The key identifies the user. It could be a random id
    //   // or something specific to a user, e.g., a cookie value
    //   { key: 'some_identifier' },
    //   // this default value will be used in offline mode or if
    //   // we cannot contact Launch Darkly
    //   // there are a helper functions for managing defaults in launchDarklyDefaults
    //   getDefaultFeatureFlag('my-flag'),
    // );

    // // then pass your flag value into the returned object of the getInitialProps function
    // // to access your flag value as a prop in the page component itself or pass it
    // // to child components of the page

    // Populate props with HFS component data
    const layout = {};
    if (services && services.layout && services.layout.getComponent) {
      layout.header = await services.layout.getComponent(vertical, 'header');
      layout.footer = await services.layout.getComponent(vertical, 'footer');
    }

    const promises = [
      store.dispatch(loadArticle(id)),
      store.dispatch(setPageView(pageView)),
      store.dispatch(setVertical(vertical)),
    ];

    if (chromeless === 'true') {
      promises.push(store.dispatch(setChromeless()));
    }
    if (lazyload === 'false') {
      promises.push(store.dispatch(disableLazyLoadAction()));
    }

    await Promise.all(promises);

    const articleStateAfterLoad = store.getState().article;
    const articleSubType = get(articleStateAfterLoad, ['content', 0, 'subType']);
    const primaryURL = get(articleStateAfterLoad, ['content', 0, 'url', 'primary'], '');
    const headline = get(articleStateAfterLoad, ['content', 0, 'headline'], {});

    if (articleSubType === externalLink) {
      ctx.res.redirect(primaryURL);
    }

    const statusCode = get(articleStateAfterLoad, ['error', 'status'], 200);
    if (statusCode >= 400) {
      ctx.res.statusCode = statusCode;
    }

    await store.dispatch(navUpdateAction({
      shareUrl: getPathName(primaryURL),
      headline,
    }));

    await store.dispatch(
      globalLoadComplete({
        domain,
        host: hostname,
        navigating: false,
        path: asPath,
        transporting: false,
      }),
    );

    return {
      chromeless,
      domain,
      host: hostname,
      layout,
      navigating: false,
      pageView,
      path: asPath,
      statusCode,
      transporting: false,
      vertical,
    };
  }

  static propTypes = {
    articles: PropTypes.arrayOf(ArticlePropType).isRequired,
    vertical: PropTypes.string.isRequired,
    isChromeless: PropTypes.bool,
    path: PropTypes.string,
    active: PropTypes.bool,
    navUpdate: PropTypes.func.isRequired,
    layout: servicesLayoutPropType.isRequired,
    statusCode: PropTypes.number.isRequired,
    themesCssPath: PropTypes.string,
  };

  static contextTypes = {
    amp: PropTypes.bool,
  };

  static defaultProps = {
    isChromeless: false,
    path: '',
    active: false,
    themesCssPath: '',
  };

  componentDidUpdate(prevProps) {
    const { active: prevActive } = prevProps;
    const { articles, active, navUpdate } = this.props;

    // UpdateTheme if active on StackCommerce articles
    if (
      get(articles, '[0].source.organization.name', false) === 'StackCommerce'
    ) {
      if (!prevActive && active) {
        navUpdate({ theme: NAVBAR_THEME.VERTICAL });
      } else if (prevActive && !active) {
        navUpdate({ theme: NAVBAR_THEME.LIGHT });
      }
    }
  }

  handleMainContentError = () => {
    const { vertical } = this.props;
    return <ErrorPage statusCode={500} vertical={vertical} />;
  };

  onView = (e) => {
    if (e && e[0] && e[0].isIntersecting) {
      const id = get(this.props, 'articles[0].id');
      BTE.trigger('articleInView', id);
    }
  };

  render() {
    const {
      articles,
      isChromeless,
      layout,
      path,
      statusCode,
      themesCssPath,
      vertical,
    } = this.props;

    if (parseInt(statusCode, 10) >= 400) {
      return (
        <ErrorPage
          statusCode={statusCode === 404 ? 404 : 500}
          vertical={vertical}
          layout={layout}
        />
      );
    }

    const article = articles && articles.length && articles[0];
    const { id: articleID } = article;

    const displayMobileBanner = getFeatureConfigForBrand(
      MOBILE_ACQUISITION_BANNER,
      vertical,
    );

    const articleHeadlineXLFontSize = publicoHeadlineBrands.includes(vertical)
      ? []
      : ['f10-xl'];
    const articleHeroHeadlineClasses = [
      'f8',
      'f9-m',
      'fw3',
      'mb3',
      'mt0',
      ...articleHeadlineXLFontSize,
    ];

    const { amp } = this.context;

    // 100000% top margin to pretend the viewport is "infinitely" tall, so you will
    // still be considered "past" the top of the article
    // -100% bottom margin to get the 0 threshold to trigger only once the top of the article
    // passes the top of the viewport
    const intObsRootMargin = '100000% 0px -100% 0px';

    const content = (
      <ErrorBoundary errorHandler={this.handleMainContentError}>
        <IntObserver
          rootMargin={intObsRootMargin}
          threshold={0}
          callback={this.onView}
        >
          <ScrollingAnalytics contentType="article" contentUrl={article.url.primary}>
            <ArticleContext.Provider value={article}>
              <GlobalMetadata />
              <ArticleMetadata article={article} vertical={vertical} />
              <div
                key={`artcleWrapper-${article.id}`}
                className={classNames(styles.article, block)}
              >
                {!amp && <PopupModule />}
                {!amp && displayMobileBanner && <MobileBanner />}

                <ArticleHero
                  content={article}
                  vertical={vertical}
                  headlineClassName={articleHeroHeadlineClasses}
                  isChromeless={isChromeless}
                />
                <ArticleBody
                  vertical={vertical}
                  article={article}
                  index={0}
                  path={path}
                  isChromeless={isChromeless}
                />
              </div>
            </ArticleContext.Provider>
          </ScrollingAnalytics>
        </IntObserver>
      </ErrorBoundary>
    );

    const wrappedContent = isChromeless ? (
      content
    ) : (
      <HeaderAndFooter layout={layout}>{content}</HeaderAndFooter>
    );

    return (
      <>
        <Head>
          {themesCssPath && (
            <link href={themesCssPath} rel="stylesheet" key={themesCssPath} />
          )}
          <IconfontStyleTag />
        </Head>

        <div
          className={classNames(
            globalContainerStyles.container,
            'bg-knockout-primary',
          )}
          id="content"
        >
          {wrappedContent}
        </div>
        <AnalyticsLaunchScripts isChromeless={isChromeless} pageID={articleID} />
        <AdsBundle />
        <UniversalCheckout vertical={vertical} />
      </>
    );
  }
}

export default navbar(navbarConfig)(
  connect(mapStateToProps, mapActionsToProps)(ArticlePage),
);
